import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import LibraryAddCheckOutlined from '@mui/icons-material/LibraryAddCheckOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';

import SelectLogo from '../../icons/field_logo.png';
import CurrentTime from '../../icons/CurrentTimeIcon';
import FutureTime from '../../icons/FutureTimeIcon';
import UserIcon from '../../icons/UserIcon';
import EmailIcon from '../../icons/EmailIcon';
import PhoneIcon from '../../icons/PhoneNumberIcon';
import DetailsIcon from '../../icons/DetailsIcon';

import { useBookingModalStyles } from './styles';
import { useBookingsContext } from '../../contexts/BookingsContext';
import useBookings from '../../hooks/useBookings';
import {
  isValidIanaTimezone,
  formatMobileNumber,
  formatPhoneNumberToE164,
} from '../../utils/helpers';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { taskListMap } from '../../global/constants/general';
import { axiosRequest } from '../../utils/axiosRequest';
import { IBookingAttachment, IUserDocumentTemplate } from '@homeporter/types';
import { COLORS } from '../../utils/colors';
import toggleToast from '../../utils/messaging';
import useBookingDescription from '../../hooks/useBookingDescription';
import { useCopilotContext } from '../../contexts/CopilotContext';
import useSession from '../../hooks/useSession';

const BookingConfirmation = ({
  handleCloseBookingModal,
  setWidth,
  setHeight,
}: {
  handleCloseBookingModal: (redirect?: string | undefined) => void;
  setWidth: (width: string) => void;
  setHeight: (height: string) => void;
}) => {
  const styles = useBookingModalStyles();
  const navigate = useNavigate();

  const {
    userInfo,
    context: selectedBookingContext,
    editBooking,
    selectedBooking,
    setSelectedBooking,
    setBookingModalUrl,
    setUserInfo,
    setEditBooking,
    profile,
    documentTemplates,
  } = useBookingsContext();

  const { formList } = useCopilotContext();

  const { description, parseBookingDescription } = useBookingDescription();

  const { createSpotBooking, updateDetails, updateBooking } = useBookings();
  const { createSession } = useSession();

  const [updateStatus, setUpdateStatus] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [date, setDate] = useState<string>('');
  const [time, setTime] = useState<string>('');

  useEffect(() => {
    if (!editBooking || !isValidIanaTimezone(editBooking.timezone_name || ''))
      return;

    setDate(
      formatInTimeZone(
        new Date(editBooking.booking_date),
        editBooking?.timezone_name || 'UTC',
        'yyyy-dd-MM',
      ),
    );

    setTime(
      formatInTimeZone(
        new Date(editBooking.booking_date),
        editBooking?.timezone_name || 'UTC',
        'HH:mm (zzz)',
      ),
    );

    parseBookingDescription(editBooking);
  }, [editBooking]);

  const handleCompleteBooking = async () => {
    console.log(`::::handleCompleteBooking...`);
    if (!editBooking || !userInfo) return;

    editBooking.payment.user.firstname = userInfo?.firstname || '';
    editBooking.payment.user.lastname = userInfo?.lastname || '';
    editBooking.payment.user.email = userInfo?.email || '';

    let selectedPlan;
    setIsUpdating(true);
    setUpdateStatus(
      editBooking.booking_id
        ? 'Updating appointment...'
        : 'Creating appointment...',
    );

    console.log('::::selectedBooking confirm', selectedBooking);

    let isNewBooking = !editBooking.booking_id;
    const originalAttachments = selectedBooking?.attachments || [];

    try {
      if (isNewBooking) {
        const resp = await createSpotBooking?.mutateAsync({
          title: editBooking?.title,
          booking_date: editBooking.booking_date,
          timezone_name: editBooking.timezone_name,
          description: `${editBooking.description}${selectedBookingContext ? `\n\nContext: ${selectedBookingContext}` : ''}`,
        });

        if (resp?.data) {
          selectedPlan = resp.data.plan;

          editBooking.booking_id = resp.data.booking.booking_id;
          editBooking.booking_secret = resp.data.booking.booking_secret;
        }
      } else {
        const resp = await updateDetails?.(editBooking);
        if (resp) {
          editBooking.booking_secret = resp.booking_secret;
          selectedPlan = editBooking.payment.plan;
        }
      }

      if (!editBooking.booking_secret)
        throw new Error('Unable to update booking');

      const payload = {
        booking_id: editBooking?.booking_id,

        body: {
          email: userInfo.email?.toLowerCase(),
          firstname: userInfo.firstname,
          lastname: userInfo.lastname,

          mobile_number: formatPhoneNumberToE164(editBooking.mobile_number),
          address: editBooking.address,
          postal_code: editBooking.postal_code.replace(/\s/g, ''),
          city: editBooking.city,
          province: editBooking.province,
          country_code: 'CA',
          plan_id: selectedPlan.plan_id || 0,
          booking_secret: editBooking.booking_secret,
          attachments: editBooking.attachments?.map((attachment) => {
            const { file, ...rest } = attachment;
            return rest;
          }),
          tasks: editBooking.tasks,
        },
      };

      const resp = await updateBooking?.mutateAsync(payload);
      console.log('::::updateBooking?.mutateAsync(payload)', payload, resp);

      if (resp?.status !== 'success')
        throw new Error('Unable to update booking');

      const tasks = Object.entries(editBooking.tasks || {}).map(
        ([taskName, isComplete]) => {
          return {
            title: taskName,
            isComplete,
          };
        },
      );

      //If editBooking.booking_id is not null, then we need to fetch the documentContents from the db and append new docs from template, do not override existing docs.

      console.log('::::editBooking', editBooking);

      if (isNewBooking) {
        const documentContents: IUserDocumentTemplate[] = [];
        tasks.forEach((task) => {
          const form = formList.find((f) => f.code === task.title);
          if (form) {
            let template = null;
            if (form.default_template_id) {
              template = documentTemplates.find(
                (t) => t.dtid === form.default_template_id,
              );
            }

            console.log('::::found template', template);

            documentContents.push({
              title: form.title,
              version: '2',
              dtid: template?.dtid || 0,
              template_name: template?.template_name || '',
              user_id: profile?.user_id || 0,
              organization_id: 0,
              sections: template
                ? JSON.parse(JSON.stringify(template.sections))
                : [],
            });
          }
        });

        if (documentContents.length > 0) {
          await createSession({
            bookingId: editBooking.booking_id,
            documentContents,
          });
        }
      }

      /*
        originalAttachments have proper urls, resp.booking.attachments have proper urls,
          attachment that is in originalAttachments but not in resp.booking.attachments need to be deleted.
          attachment that is in resp.booking.attachments but not in originalAttachments need to be uploaded using attachemnt.file that is in editBooking.attachments.
          attachment that is in both originalAttachments and resp.booking.attachments are ignored.
          create arrays of attachment URLs for comparison
      */

      const respAttachmentUrls = resp.booking.attachments.map(
        (a: IBookingAttachment) => a.url,
      );

      const toDelete = originalAttachments
        .filter((a: IBookingAttachment) => {
          console.log('::::a.url', a.url, !respAttachmentUrls.includes(a.url));
          return !respAttachmentUrls.includes(a.url);
        })
        .map((a: IBookingAttachment) => ({
          file: null,
          url: a.url,
        }));

      const originalAttachmentNames = (originalAttachments || []).map(
        (a: IBookingAttachment) => a.name,
      );

      const toUpload = (
        editBooking.attachments?.filter(
          (a) => a.file && !originalAttachmentNames.includes(a.name),
        ) || []
      ).map((attachment) => {
        const responseAttachment = resp.booking.attachments.find(
          (a: IBookingAttachment) => a.name === attachment.name,
        );
        return {
          file: attachment.file,
          url: responseAttachment?.url || attachment.url,
        };
      });

      const combinedAttachments = [...toDelete, ...toUpload];

      console.log('::::Attachments to delete:', toDelete);
      console.log('::::Attachments to upload:', toUpload);
      console.log('::::combinedAttachments', combinedAttachments);
      /*

        Need to add logic to replace and skip attachments that are already in the booking.

      */

      if (combinedAttachments?.length) {
        const axios = axiosRequest();
        for (let i = 0; i < combinedAttachments.length; i++) {
          const attachment = combinedAttachments[i];
          console.log('::::attachment', attachment);

          setUpdateStatus(
            `${attachment.file ? 'Uploading' : 'Deleting'} attachment ${i + 1} of ${combinedAttachments.length}`,
          );
          await new Promise((resolve) => setTimeout(resolve, 500));

          try {
            const formData = new FormData();
            if (attachment.file) formData.append('file', attachment.file);
            formData.append('url', attachment.url);

            const response = await axios.post(
              '/bookings/upload-attachment',
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent: any) => {
                  const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total,
                  );
                  setUpdateStatus(
                    `${attachment.file ? 'Uploading' : 'Deleting'} attachment ${i + 1} (${percentCompleted}%)`,
                  );
                },
              },
            );

            console.log('::::response', response);
            if (response.data.status !== 'success') {
              throw new Error('Failed to process attachment');
            }
          } catch (err) {
            throw new Error('Failed to process attachment');
          }
        }
      }

      setEditBooking(resp.booking);
      setSelectedBooking(resp.booking);

      setWidth('30rem');
      setHeight('15rem');

      setUserInfo({
        ...resp.userDetails,
      });
    } catch (error) {
      console.log(error);
      toggleToast('An error occurred while updating the booking', 'error');
    }

    setIsCompleted(true);
    setIsUpdating(false);
  };

  if (!editBooking) return <>Unable to find booking</>;
  return (
    <Box sx={styles.container}>
      <Typography sx={{ ...styles.title, flexShrink: 0 }}>
        <Button
          sx={{
            ...styles.button,
            ...styles.close,
          }}
          onClick={() => handleCloseBookingModal()}>
          Close
        </Button>
        {isCompleted ? '' : 'Confirm Details'}
      </Typography>
      {isCompleted ? (
        <Paper id="booking-confirmation-paper" elevation={0} sx={styles.paper}>
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              fontWeight: 'bold',
            }}>
            Appointment Completed!
          </Typography>
        </Paper>
      ) : (
        <Paper
          id="booking-confirmation-paper"
          elevation={0}
          sx={{ ...styles.paper }}>
          <Typography style={styles.subHeading}>SESSION DETAILS</Typography>
          <Grid container spacing={2} sx={styles.grid}>
            <Grid item xs={5.5}>
              <Box sx={styles.information}>
                <img
                  width={20}
                  height={20}
                  src={SelectLogo}
                  alt="Select Logo"
                />
                <Typography>
                  {editBooking?.title ? editBooking.title : 'Undefined'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={5.5}>
              <Box sx={styles.information}>
                <CurrentTime />
                <Typography>{date}</Typography>
              </Box>
            </Grid>
            <Grid item xs={5.5}>
              <Box sx={styles.information}></Box>
            </Grid>
            <Grid item xs={5.5}>
              <Box sx={styles.information}>
                <FutureTime />
                <Typography>{time ? time : 'N/A'}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography style={styles.subHeading}>
            PERSONAL INFORMATION
          </Typography>
          <Grid container spacing={2} sx={styles.grid}>
            <Grid item xs={5.5}>
              <Box sx={styles.information}>
                <UserIcon />
                <Typography
                  title={`${userInfo?.firstname || ''} ${!!userInfo?.lastname ? ` ${userInfo?.lastname}` : ''}`}>
                  {userInfo?.firstname || userInfo?.lastname
                    ? `${userInfo?.firstname || ''} ${!!userInfo?.lastname ? ` ${userInfo?.lastname}` : ''}`
                    : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={5.5}>
              <Box sx={styles.information}></Box>
            </Grid>
            <Grid item xs={5.5}>
              <Box sx={styles.information}>
                <EmailIcon />
                <Typography title={userInfo?.email || ''}>
                  {userInfo?.email ? userInfo.email : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={5.5}>
              <Box sx={styles.information}>
                <PhoneIcon />
                <Typography title={editBooking?.mobile_number || ''}>
                  {editBooking?.mobile_number
                    ? formatMobileNumber(editBooking.mobile_number)
                    : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography style={styles.subHeading}>HOME INFORMATION</Typography>
          <Grid
            container
            spacing={2}
            sx={{
              ...styles.grid,
              borderBottom:
                !!editBooking?.description ||
                editBooking?.attachments?.[0] ||
                Object.keys(editBooking?.tasks || {})[0]
                  ? '.0625rem solid lightgray'
                  : '0',
            }}>
            <Grid item xs={11}>
              <Box sx={styles.information}>
                <LocationOnOutlined
                  sx={{
                    color: COLORS.MEDIUM_GRAY,
                    fontSize: '1.5rem',
                    alignSelf: 'flex-start',
                  }}
                />
                <Typography>
                  {editBooking?.address ? editBooking.address : 'N/A'}
                  {editBooking?.city ? `, ${editBooking.city}` : ''}
                  {editBooking?.province ? `, ${editBooking.province}` : ''}
                  {editBooking?.postal_code
                    ? `, ${editBooking.postal_code}`
                    : ''}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {editBooking?.attachments && editBooking?.attachments?.length > 0 && (
            <>
              <Typography style={styles.subHeading}>ATTACHMENTS</Typography>
              <Grid
                container
                spacing={2}
                sx={{
                  ...styles.grid,
                  borderBottom:
                    !!editBooking?.description ||
                    Object.keys(editBooking?.tasks || {})[0]
                      ? '.0625rem solid lightgray'
                      : '0',
                }}>
                <Grid item xs={5.5}>
                  <Box sx={styles.information}>
                    <AttachFileIcon sx={{ alignSelf: 'flex-start' }} />
                    <Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}>
                        {editBooking.attachments.map((attachment, index) => (
                          <Typography
                            key={index}
                            title={attachment.name}
                            noWrap
                            sx={{
                              width: '100%',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              fontSize: '0.2rem',
                            }}>
                            {attachment.name}
                          </Typography>
                        ))}
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5.5}></Grid>
              </Grid>
            </>
          )}

          {Object.keys(editBooking?.tasks || {})?.length > 0 && (
            <>
              <Typography style={styles.subHeading}>WORK OBJECTS</Typography>
              <Grid
                container
                spacing={2}
                sx={{
                  ...styles.grid,
                  borderBottom: !editBooking?.description
                    ? '0'
                    : '.0625rem solid lightgray',
                }}>
                <Grid item xs={5.5}>
                  <Box sx={styles.information}>
                    <LibraryAddCheckOutlined sx={{ alignSelf: 'flex-start' }} />

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}>
                      {Object.entries(editBooking?.tasks || {}).map(
                        ([taskName, isComplete], index) => (
                          <Typography
                            key={index}
                            title={taskName}
                            noWrap
                            sx={styles.taskName}>
                            {taskName}:{' '}
                            {
                              taskListMap.find(
                                (action) => action.code === taskName,
                              )?.action
                            }
                          </Typography>
                        ),
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={5.5}></Grid>
              </Grid>
            </>
          )}

          {editBooking?.description && (
            <>
              <Typography style={styles.subHeading}>
                ADDITIONAL NOTES
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{ ...styles.grid, borderBottom: 0 }}>
                <Grid item xs={11}>
                  <Box sx={styles.description}>
                    <DetailsIcon />
                    <Typography sx={{ paddingLeft: '.5rem' }}>
                      {description}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}

      <Box sx={styles.buttonContainer}>
        {!isCompleted && (
          <Button
            sx={{
              ...styles.button,
            }}
            onClick={() => setBookingModalUrl(`booking-details`)}>
            Back
          </Button>
        )}
        {!isCompleted && (
          <Button
            sx={{
              ...styles.button,
              ...styles.buttonPrimary,
              color: '#fff!important',
            }}
            disabled={isUpdating}
            onClick={handleCompleteBooking}>
            {isUpdating ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ color: '#fff', mr: '0.5rem' }}
                />
                {updateStatus}
              </>
            ) : editBooking.booking_id ? (
              'Update Appointment'
            ) : (
              'Create Appointment'
            )}
          </Button>
        )}

        {isCompleted && (
          <Button
            sx={{
              ...styles.button,
              ...styles.buttonPrimary,
            }}
            onClick={() => {
              handleCloseBookingModal(
                `inspector/${editBooking.booking_session_code}`,
              );
            }}>
            Start Session
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default BookingConfirmation;
